import {
	getLanguageFromIntlLocale,
	getLowercaseCountryCodeFromIntlLocale,
	localeToIntlLocale,
	localeValuesToIntlLocale,
} from "@core/i18n/locale-helper";
import { TranslationsManager } from "@core/i18n/translations-manager";
import { getLogger } from "@core/logging/logger";
import { Service } from "@core/services/service";
import { LocalStorageKeys } from "@core/storage/local-storage-keys";
import { storage } from "@core/storage/storage";

import { CountryService } from "@domain/localization/country-service";
import { UserManager } from "@domain/user/user-manager";
import { Observable, observable } from "micro-observables";
import { addLocale, locale as setPrimeReactLocale } from "primereact/api";

export const DEFAULT_LOCALE = "en-US";

export class LocaleManager implements Service {
	private readonly logger = getLogger("🌐 [LocaleManager]");
	private readonly _localeStore = storage.getStore<string, string>(
		LocalStorageKeys.locale,
		v => v,
		v => v,
	);
	private readonly _locale = observable<string>(DEFAULT_LOCALE);
	locale = this._locale.readOnly();

	_defaultLocalesMapping = <Record<string, string | undefined>>{};

	private readonly _supportedLocales = observable<Map<string, string[]>>(new Map());
	readonly supportedLocales = this._supportedLocales.readOnly();

	countryCode: Observable<string>;

	constructor(
		private readonly userManager: UserManager,
		private readonly translationsManager: TranslationsManager,
		private readonly countryService: CountryService,
	) {
		this.countryCode = this.locale.select(newLocale => getLowercaseCountryCodeFromIntlLocale(newLocale));

		translationsManager.availableLocales.subscribe(locales => {
			this.updatePrimeWording(locales);
			this.setUserLocaleOrDefault();
		});

		userManager.user.subscribe(() => {
			this.setUserLocaleOrDefault();
		});
	}

	async init(): Promise<void> {
		const defaultLocalesMapping = await this.countryService.getDefaultLocalesMapping();
		this._defaultLocalesMapping = localeValuesToIntlLocale(defaultLocalesMapping);
		this.setUserLocaleOrDefault();
	}

	async fetchSupportedLocales(): Promise<void> {
		const supportedLocales = await this.countryService.getSupportedLocales();
		this._supportedLocales.set(supportedLocales);
	}

	setUserLocaleOrDefault(): void {
		const userLocale = this.userManager.user.get()?.locale;
		const navigatorLocale = this.resolveLocaleFromNavigator();
		const userLocaleOrDefault = userLocale ?? this._localeStore.getItem(navigatorLocale);

		this.logger.debug("setUserLocaleOrDefault", {
			userLocale,
			localStorageLocale: this._localeStore.getItem(),
			navigatorLocale,
		});

		this.changeLocale(userLocaleOrDefault);
	}

	getNavigatorLocale(): string {
		return navigator.languages[0];
	}

	resolveLocale(locale: string): string {
		const intlLocale = localeToIntlLocale(locale);
		return this.getBestLocaleAvailable([intlLocale]);
	}

	resolveLocaleFromNavigator(): string {
		return this.getBestLocaleAvailable(Array.from(navigator.languages));
	}

	resolveLocaleForCountry(countryCode: string): string | undefined {
		const localesForCountry = this._supportedLocales.get().get(countryCode);
		if (localesForCountry?.length) {
			const resolved = this.getBestLocaleAvailable(localesForCountry);
			if (resolved !== DEFAULT_LOCALE) return resolved;
		}
		const availableLocales = this.translationsManager.availableLocales.get();
		const countryLocales = availableLocales.filter(
			locale => getLowercaseCountryCodeFromIntlLocale(locale) === countryCode,
		);
		if (countryLocales.length) {
			const resolved = this.getBestLocaleAvailable(countryLocales);
			if (resolved !== DEFAULT_LOCALE) return resolved;
		}
		return undefined;
	}

	changeLocale(locale: string): void {
		const intlLocale = localeToIntlLocale(locale);
		const handledLocale = this.getBestLocaleAvailable([intlLocale]);
		if (intlLocale !== handledLocale) {
			this.logger.warn(
				"Trying to change locale to",
				intlLocale,
				"but the best-matching available locale is",
				handledLocale,
			);
		}
		this.logger.debug("Changing locale to", handledLocale);
		this._localeStore.setItem(handledLocale);
		this._locale.set(handledLocale);
		setPrimeReactLocale(handledLocale);
	}

	private updatePrimeWording(locales: string[]): void {
		// init primereact locales for calendar
		this.logger.debug("Updating prime wordings");
		locales.forEach(locale => {
			const translations = this.translationsManager.getLocaleWordings(locale);
			const localeOptions = {
				clear: translations["primereact.calendar.clear"],
				dateFormat: translations["primereact.calendar.dateFormat"],
				dayNames: translations["primereact.calendar.dayNames"].split("_"),
				dayNamesMin: translations["primereact.calendar.dayNamesMin"].split("_"),
				dayNamesShort: translations["primereact.calendar.dayNamesShort"].split("_"),
				firstDayOfWeek: this.getFixedFirstDayOfWeekIndex(translations["primereact.calendar.firstDayOfWeek"]),
				monthNames: translations["primereact.calendar.monthNames"].split("_"),
				monthNamesShort: translations["primereact.calendar.monthNamesShort"].split("_"),
				today: translations["primereact.calendar.today"],
			};
			addLocale(locale, localeOptions);
		});
	}

	private getFixedFirstDayOfWeekIndex(firstDayOfWeekLocalizedValue: string): number {
		// Fix for lokalize that gives us "" instead of "0" for localized index values of 0
		const index = parseInt(firstDayOfWeekLocalizedValue);
		if (!isNaN(index)) {
			return index;
		}
		return 0;
	}

	// eslint-disable-next-line sonarjs/cognitive-complexity
	private getBestLocaleAvailable(givenLocales: string[]): string {
		const availableLocales = this.translationsManager.availableLocales.get();
		const resolveFromAvailableOrMapped = (locale: string): string | undefined =>
			availableLocales.includes(locale) ? locale : this._defaultLocalesMapping[locale];

		for (const locale of givenLocales) {
			// 1. Exact match or mapped match
			const resolvedLocale = resolveFromAvailableOrMapped(locale);
			if (resolvedLocale) return resolvedLocale;

			// 2. Match by same language in givenLocales
			const language = getLanguageFromIntlLocale(locale, true);
			for (const otherLocale of givenLocales) {
				if (otherLocale !== locale && getLanguageFromIntlLocale(otherLocale, true) === language) {
					const resolvedOther = resolveFromAvailableOrMapped(otherLocale);
					if (resolvedOther) return resolvedOther;
				}
			}

			// 3. Match by same language in availableLocales
			const availableLocale = availableLocales.find(loc => getLanguageFromIntlLocale(loc, true) === language);
			if (availableLocale) return availableLocale;
		}

		return DEFAULT_LOCALE;
	}
}
